// TL-6 Line/Polyline: click-chain, close on first point (SNP-7), Enter/right-click/dblclick
// finish, Backspace removes last, Measurements `length` or `length<angle`.
import { dir, dist, angleOf, sub, type Vec } from "../core/geometry";
import { parseLengthAngle, formatLength } from "../core/units";
import { newId } from "../doc/document";
import type { PolylineEntity } from "../doc/entities";
import { worldToScreen } from "../engine/camera";
import type { RenderView } from "../engine/renderEntities";
import { strokeWidthWorld, dashPattern } from "../engine/renderEntities";
import type { Tool, ToolCtx, ToolKeyEvent, ToolPointerEvent } from "./types";

export class LineTool implements Tool {
  readonly id = "line" as const;
  private points: Vec[] = [];
  private cursor: Vec | null = null;

  constructor(private ctx: ToolCtx) {}

  activate(): void {
    this.reset();
  }

  deactivate(): void {
    this.reset();
  }

  cancel(): void {
    this.reset();
  }

  inProgress(): boolean {
    return this.points.length > 0;
  }

  private reset(): void {
    this.points = [];
    this.cursor = null;
    this.ctx.setSnapBase(null);
    this.ctx.setSelfClose(null);
    this.ctx.setMeasure("length", "length or length<angle");
    this.ctx.setHint("Line: click the first point · Esc = cancel");
    this.ctx.setCursorTip(null);
    this.ctx.requestRender();
  }

  private syncState(): void {
    const n = this.points.length;
    this.ctx.setSnapBase(n > 0 ? this.points[n - 1]! : null);
    this.ctx.setSelfClose(n >= 3 ? this.points[0]! : null); // closing needs ≥3 vertices (INV-4)
    if (n === 0) {
      this.ctx.setHint("Line: click the first point · Esc = cancel");
      this.ctx.setCursorTip(null);
    } else if (n < 3) {
      this.ctx.setHint("Line: click next point — type a length ⏎ · Shift = lock axis · Enter/right-click = finish");
      this.ctx.setCursorTip("next point · length ⏎");
    } else {
      this.ctx.setHint("Line: click next point, click the first point to close, Enter = finish · Backspace = undo point");
      this.ctx.setCursorTip("next point · start = close · ⏎");
    }
    this.ctx.requestRender();
  }

  pointerDown(e: ToolPointerEvent): void {
    if (e.button === 2) {
      this.finish(false);
      return;
    }
    if (e.button !== 0) return;
    this.addPoint(e.point);
  }

  private addPoint(p: Vec): void {
    const last = this.points[this.points.length - 1];
    // Close when clicking the first point (SNP-7 gives us its exact coords).
    if (this.points.length >= 3 && dist(p, this.points[0]!) < 1e-9) {
      this.finish(true);
      return;
    }
    if (last && dist(p, last) < 1e-9) return; // EC-TL-5 zero-length segment ignored
    this.points.push(p);
    this.syncState();
  }

  pointerMove(e: ToolPointerEvent): void {
    this.cursor = e.point;
    this.ctx.requestRender();
  }

  pointerUp(): void {}

  doubleClick(): void {
    this.finish(false);
  }

  key(e: ToolKeyEvent): boolean {
    if (e.key === "Enter") {
      this.finish(false);
      return true;
    }
    if (e.key === "Backspace" || e.key === "Delete") {
      if (this.points.length > 0) {
        this.points.pop();
        this.syncState();
        return true;
      }
    }
    if (e.key === "Escape" && this.points.length > 0) {
      this.reset();
      return true;
    }
    return false;
  }

  measurement(raw: string): boolean {
    const { units } = this.ctx.docUnits();
    const parsed = parseLengthAngle(raw, units);
    if (!parsed || this.points.length === 0) return false;
    const last = this.points[this.points.length - 1]!;
    let d: Vec;
    if (parsed.angleDeg !== null) {
      d = dir(parsed.angleDeg);
    } else {
      const toward = this.cursor && dist(this.cursor, last) > 1e-9 ? this.cursor : null;
      d = toward ? dir(angleOf(sub(toward, last))) : dir(0);
    }
    this.addPoint({ x: last.x + d.x * parsed.length, y: last.y + d.y * parsed.length });
    return true;
  }

  private finish(closed: boolean): void {
    const pts = this.points;
    if (pts.length < 2 || (closed && pts.length < 3)) {
      // EC-TL-4: silent cancel when nothing valid was placed
      this.reset();
      return;
    }
    const doc = this.ctx.store.doc;
    const entity: PolylineEntity = {
      id: newId(),
      type: "polyline",
      layerId: this.ctx.activeLayerId(),
      locked: false,
      hidden: false,
      points: pts,
      closed,
      style: structuredClone(doc.defaultStyle)
    };
    this.ctx.store.dispatch({
      kind: "addEntities",
      entities: [entity],
      z: [{ layerId: entity.layerId, index: -1 }]
    });
    // TL-4 post-commit re-edit: retype a length to resize the LAST segment.
    const entityId = entity.id;
    const frozen = pts.map((p) => ({ ...p }));
    this.ctx.registerReapply((rawValue) => {
      const { units } = this.ctx.docUnits();
      const p = parseLengthAngle(rawValue, units);
      if (!p || frozen.length < 2) return false;
      const a = frozen[frozen.length - 2]!;
      const b = frozen[frozen.length - 1]!;
      const ang = p.angleDeg ?? angleOf(sub(b, a));
      const nd = dir(ang);
      const newPts = [...frozen.slice(0, -1), { x: a.x + nd.x * p.length, y: a.y + nd.y * p.length }];
      this.ctx.store.dispatch({
        kind: "updateEntities",
        patches: [{ id: entityId, before: { points: frozen }, after: { points: newPts } }]
      });
      return true;
    });
    this.reset();
  }

  renderDynamic(v: RenderView): void {
    if (this.points.length === 0) return;
    const doc = this.ctx.store.doc;
    const style = doc.defaultStyle;
    const { ctx } = v;
    ctx.strokeStyle = style.strokeColor;
    ctx.lineWidth = strokeWidthWorld(style, v.cam, v.dpr);
    ctx.lineCap = "round";
    ctx.setLineDash(dashPattern(style, style.strokeWidth, v.cam));
    ctx.beginPath();
    ctx.moveTo(this.points[0]!.x, this.points[0]!.y);
    for (let i = 1; i < this.points.length; i++) ctx.lineTo(this.points[i]!.x, this.points[i]!.y);
    if (this.cursor) ctx.lineTo(this.cursor.x, this.cursor.y);
    ctx.stroke();
    ctx.setLineDash([]);
    // live readout: current segment length + angle (04 RND live dimension)
    if (this.cursor) {
      const last = this.points[this.points.length - 1]!;
      const len = dist(this.cursor, last);
      if (len > 1e-9) {
        const { units, precision } = this.ctx.docUnits();
        const ang = angleOf(sub(this.cursor, last));
        drawReadout(v, this.cursor, `${formatLength(len, units, precision)}  ${ang.toFixed(1)}°`);
      }
    }
  }
}

/** Screen-space readout near a world point (shared by draw tools). */
/**
 * The screen boxes of the readouts that the tools drew (CSS pixels). The cursor tip reads them so that it never sits on a
 * readout. A tool draws its readout on the dynamic layer and the tip is drawn on the overlay layer: two canvases that are
 * painted at different times, so the boxes are kept for each canvas, and a layer clears its own when it starts to paint.
 */
export interface ReadoutBox {
  x: number;
  y: number;
  w: number;
  h: number;
}
const readoutBoxes = new Map<unknown, ReadoutBox[]>();

/** Forget the readouts of one layer. A layer calls this before the active tool paints on it. */
export function clearReadoutBoxes(v: RenderView): void {
  readoutBoxes.delete(v.ctx.canvas);
}

/** The readouts of every layer, as they were painted last. */
export function drawnReadoutBoxes(): readonly ReadoutBox[] {
  return [...readoutBoxes.values()].flat();
}

export function drawReadout(v: RenderView, world: Vec, text: string): void {
  const s = worldToScreen(v.cam, v.cssW, v.cssH, world);
  const { ctx } = v;
  ctx.save();
  ctx.setTransform(v.dpr, 0, 0, v.dpr, 0, 0);
  ctx.font = "12px Inter, sans-serif";
  const w = ctx.measureText(text).width;
  const mine = readoutBoxes.get(v.ctx.canvas) ?? [];
  mine.push({ x: s.x + 12, y: s.y + 12, w: w + 10, h: 18 });
  readoutBoxes.set(v.ctx.canvas, mine);
  ctx.fillStyle = "rgba(20, 22, 28, 0.85)";
  ctx.fillRect(s.x + 12, s.y + 12, w + 10, 18);
  ctx.fillStyle = "#e8eaf0";
  ctx.fillText(text, s.x + 17, s.y + 25);
  ctx.restore();
}
